import { useCallback, useState } from 'react';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toastNeedsAttentionHidden } from '@gokrew/ui';
import { hideNeedsAttentionLocally } from '@gokrew/ui/utility/needsAttention';
import { QueryKeys, TranslationNS } from '@gokrew/models/constants';
import type { ApiResponse } from '@gokrew/models/generic';
import type {
  NeedsAttentionFeed,
  NeedsAttentionHideOptions,
} from '@gokrew/models/company-dashboard';
import type {
  CreateMyTeamDelegationPayload,
  MyTeamCalendarParams,
  MyTeamDecision,
  MyTeamLeaveRequestsParams,
  MyTeamListParams,
  MyTeamProbationPayload,
  MyTeamTimesheetApprovalKind,
  TeamTimeOffParams,
} from '@gokrew/models/my-team';
import {
  createMyTeamDelegation,
  decideMyTeamLeave,
  decideMyTeamTimesheet,
  getMyTeamTimesheetApprovals,
  getMyTeamTimesheetApprovalsCount,
  exportMyTeam,
  getMyTeam,
  getMyTeamAccess,
  getMyTeamApprovals,
  getMyTeamApprovalContext,
  getMyTeamApprovalsCount,
  getMyTeamChangeRequests,
  getMyTeamChangeRequestsCount,
  decideMyTeamChangeRequest,
  getMyTeamCalendar,
  getMyTeamCovering,
  getMyTeamDelegateCandidates,
  getMyTeamDelegations,
  getMyTeamMember,
  getMyTeamMemberBalances,
  getMyTeamMemberProbation,
  actOnMyTeamProbation,
  getMyTeamMemberLeaveRequests,
  getMyTeamSummary,
  getMyTeamAttention,
  getTeamOverview,
  hideMyTeamAttention,
  pingMyTeamAttention,
  unhideMyTeamAttention,
  getTeamTimeOff,
  revokeMyTeamDelegation,
} from '@/api/my-team';
import { useEmployeePortalAuth } from '@/context/EmployeeAuthContext';

/*
 * Errors are not toasted here: the API client's response interceptor already
 * shows the backend's translated message. Every key carries the signed-in
 * employee id, so a mock-login switch never reads the previous person's team.
 */

/** Whether to show My team at all. Never errors for a plain employee. */
export function useMyTeamAccess() {
  const { user } = useEmployeePortalAuth();
  return useQuery({
    queryKey: [QueryKeys.MyTeamAccess, user?.id],
    queryFn: getMyTeamAccess,
    enabled: !!user,
    staleTime: 60_000,
    select: (res) => res.data,
  });
}

/** Only fires once the access probe says the caller is a manager. */
function useIsManager() {
  const { data } = useMyTeamAccess();
  return !!data?.enabled && !!data?.isManager;
}

/**
 * Whether the caller has an Approvals inbox: a manager, or someone covering
 * a manager's approvals right now (a delegate sees only the delegated
 * requests, never the team, decision D10).
 */
export function useCanSeeApprovals() {
  const { data } = useMyTeamAccess();
  return !!data?.enabled && (!!data?.isManager || !!data?.isDelegate);
}

/**
 * The Team tab every employee has (D49): my manager, my teammates and my
 * direct reports. Not gated on manager access: a plain employee reads it too.
 */
export function useTeamOverview() {
  const { user } = useEmployeePortalAuth();
  return useQuery({
    queryKey: [QueryKeys.TeamOverview, user?.id],
    queryFn: getTeamOverview,
    enabled: !!user,
    staleTime: 60_000,
    select: (res) => res.data,
  });
}

/** "Who's off": approved leave only, the server picks team or company. */
export function useTeamTimeOff(params: TeamTimeOffParams = {}) {
  const { user } = useEmployeePortalAuth();
  return useQuery({
    queryKey: [QueryKeys.TeamTimeOff, user?.id, params],
    queryFn: () => getTeamTimeOff(params),
    enabled: !!user,
    staleTime: 60_000,
    select: (res) => res.data,
  });
}

/** Every cache a decision changes: the inbox, its badge and the counts. */
const APPROVAL_KEYS = [
  QueryKeys.MyTeamApprovals,
  QueryKeys.MyTeamApprovalsCount,
  QueryKeys.MyTeamSummary,
  QueryKeys.MyTeamAttention,
  QueryKeys.MyTeamCalendar,
  QueryKeys.MyTeamMemberLeaveRequests,
  QueryKeys.MyTeamMemberBalances,
  QueryKeys.MyTeamList,
  QueryKeys.TeamOverview,
  QueryKeys.TeamTimeOff,
];

export function useMyTeamApprovals() {
  const { user } = useEmployeePortalAuth();
  const enabled = useCanSeeApprovals();
  return useQuery({
    queryKey: [QueryKeys.MyTeamApprovals, user?.id],
    queryFn: getMyTeamApprovals,
    enabled: !!user && enabled,
    select: (res) => res.data,
  });
}

/** Decision context of one request in the inbox (opened in its dialog). */
export function useMyTeamApprovalContext(leaveRequestId: string) {
  const { user } = useEmployeePortalAuth();
  return useQuery({
    queryKey: [
      QueryKeys.MyTeamApprovals,
      QueryKeys.LeaveDecisionContext,
      leaveRequestId,
      user?.id,
    ],
    queryFn: () => getMyTeamApprovalContext(leaveRequestId),
    enabled: !!user && !!leaveRequestId,
    select: (res) => res.data,
  });
}

/** The nav badge: refreshed every minute and on focus. */
export function useMyTeamApprovalsCount() {
  const { user } = useEmployeePortalAuth();
  const enabled = useCanSeeApprovals();
  return useQuery({
    queryKey: [QueryKeys.MyTeamApprovalsCount, user?.id],
    queryFn: getMyTeamApprovalsCount,
    enabled: !!user && enabled,
    refetchInterval: 60_000,
    select: (res) => res.data.pending,
  });
}

/**
 * Profile changes of the manager's direct reports routed to them by the
 * company's change rules (manager approval). Managers only.
 */
export function useMyTeamChangeRequests() {
  const { user } = useEmployeePortalAuth();
  const isManager = useIsManager();
  return useQuery({
    queryKey: [QueryKeys.MyTeamChangeRequests, user?.id],
    queryFn: getMyTeamChangeRequests,
    enabled: !!user && isManager,
    select: (res) => res.data,
  });
}

/** The change requests part of the approvals badge. */
export function useMyTeamChangeRequestsCount() {
  const { user } = useEmployeePortalAuth();
  const isManager = useIsManager();
  return useQuery({
    queryKey: [QueryKeys.MyTeamChangeRequestsCount, user?.id],
    queryFn: getMyTeamChangeRequestsCount,
    enabled: !!user && isManager,
    refetchInterval: 60_000,
    select: (res) => res.data.pending,
  });
}

/** Approve, or reject with a reason; every outcome refreshes the inbox. */
export function useDecideMyTeamChangeRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: string;
      decision: MyTeamDecision;
      reason?: string;
    }) => decideMyTeamChangeRequest(vars.id, vars.decision, vars.reason),
    onSettled: () =>
      Promise.all(
        [
          QueryKeys.MyTeamChangeRequests,
          QueryKeys.MyTeamChangeRequestsCount,
          QueryKeys.MyTeamMember,
        ].map((key) => queryClient.invalidateQueries({ queryKey: [key] }))
      ),
  });
}

/**
 * Timesheet weeks and changes the caller may decide: a manager whose company
 * routes timesheets to managers, a manager they escalated to, or a delegate.
 */
export function useMyTeamTimesheetApprovals() {
  const { user } = useEmployeePortalAuth();
  const enabled = useCanSeeApprovals();
  return useQuery({
    queryKey: [QueryKeys.MyTeamTimesheetApprovals, user?.id],
    queryFn: getMyTeamTimesheetApprovals,
    enabled: !!user && enabled,
    select: (res) => res.data,
  });
}

/** The timesheet part of the approvals badge. */
export function useMyTeamTimesheetApprovalsCount() {
  const { user } = useEmployeePortalAuth();
  const enabled = useCanSeeApprovals();
  return useQuery({
    queryKey: [QueryKeys.MyTeamTimesheetApprovalsCount, user?.id],
    queryFn: getMyTeamTimesheetApprovalsCount,
    enabled: !!user && enabled,
    refetchInterval: 60_000,
    select: (res) => res.data.pending,
  });
}

/**
 * Approve or send back one week or change. Errors are left to the caller,
 * which explains a 403/409 in place; every outcome refreshes the inbox.
 */
export function useDecideMyTeamTimesheet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      kind: MyTeamTimesheetApprovalKind;
      id: string;
      decision: MyTeamDecision;
      note?: string;
    }) => decideMyTeamTimesheet(vars.kind, vars.id, vars.decision, vars.note),
    onSettled: () =>
      Promise.all(
        [
          QueryKeys.MyTeamTimesheetApprovals,
          QueryKeys.MyTeamTimesheetApprovalsCount,
          QueryKeys.MyTeamAttention,
        ].map((key) => queryClient.invalidateQueries({ queryKey: [key] }))
      ),
  });
}

/** Whose approvals the caller is covering right now. */
export function useMyTeamCovering() {
  const { user } = useEmployeePortalAuth();
  const enabled = useCanSeeApprovals();
  return useQuery({
    queryKey: [QueryKeys.MyTeamCovering, user?.id],
    queryFn: getMyTeamCovering,
    enabled: !!user && enabled,
    select: (res) => res.data,
  });
}

/**
 * Approve or reject one request. Errors are left to the caller: the Approvals
 * tab explains a 409 ("moved to X", "already decided") in place, and the
 * request is quiet so the global toast does not repeat it. Every outcome
 * refreshes the inbox, since a refusal also means the list is stale.
 */
export function useDecideMyTeamLeave() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      leaveRequestId: string;
      decision: MyTeamDecision;
      comment?: string;
    }) => decideMyTeamLeave(vars.leaveRequestId, vars.decision, vars.comment),
    onSettled: () =>
      Promise.all(
        APPROVAL_KEYS.map((key) =>
          queryClient.invalidateQueries({ queryKey: [key] })
        )
      ),
  });
}

export function useMyTeamDelegations() {
  const { user } = useEmployeePortalAuth();
  const isManager = useIsManager();
  return useQuery({
    queryKey: [QueryKeys.MyTeamDelegations, user?.id],
    queryFn: getMyTeamDelegations,
    enabled: !!user && isManager,
    select: (res) => res.data,
  });
}

export function useMyTeamDelegateCandidates(search: string) {
  const { user } = useEmployeePortalAuth();
  const isManager = useIsManager();
  return useQuery({
    queryKey: [QueryKeys.MyTeamDelegateCandidates, user?.id, search],
    queryFn: () => getMyTeamDelegateCandidates(search),
    enabled: !!user && isManager,
    placeholderData: keepPreviousData,
    select: (res) => res.data,
  });
}

export function useCreateMyTeamDelegation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateMyTeamDelegationPayload) =>
      createMyTeamDelegation(payload),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MyTeamDelegations],
      }),
  });
}

export function useRevokeMyTeamDelegation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (delegationId: string) => revokeMyTeamDelegation(delegationId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MyTeamDelegations],
      }),
  });
}

export function useMyTeamList(params: MyTeamListParams) {
  const { user } = useEmployeePortalAuth();
  const isManager = useIsManager();
  return useQuery({
    queryKey: [QueryKeys.MyTeamList, user?.id, params],
    queryFn: () => getMyTeam(params),
    enabled: !!user && isManager,
    placeholderData: keepPreviousData,
    select: (res) => res.data,
  });
}

/**
 * The manager's team a page at a time, searchable: a subtree can be the
 * whole company, so pickers never stop at one page of it.
 */
export function useInfiniteMyTeam(params: Omit<MyTeamListParams, 'page'>) {
  const { user } = useEmployeePortalAuth();
  const isManager = useIsManager();
  return useInfiniteQuery({
    queryKey: [QueryKeys.MyTeamList, user?.id, 'infinite', params],
    queryFn: ({ pageParam }) => getMyTeam({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.data.meta.hasNextPage ? lastPage.data.meta.page + 1 : undefined,
    enabled: !!user && isManager,
  });
}

export function useMyTeamSummary() {
  const { user } = useEmployeePortalAuth();
  const isManager = useIsManager();
  return useQuery({
    queryKey: [QueryKeys.MyTeamSummary, user?.id],
    queryFn: getMyTeamSummary,
    enabled: !!user && isManager,
    select: (res) => res.data,
  });
}

/** The manager's "Needs attention" feed on My team (spec 1.3). */
export function useMyTeamAttention() {
  const { user } = useEmployeePortalAuth();
  const isManager = useIsManager();
  return useQuery({
    queryKey: [QueryKeys.MyTeamAttention, user?.id],
    queryFn: getMyTeamAttention,
    enabled: !!user && isManager,
    select: (res) => res.data,
  });
}

export function useMyTeamMember(employeeId: string | undefined) {
  const { user } = useEmployeePortalAuth();
  const isManager = useIsManager();
  return useQuery({
    queryKey: [QueryKeys.MyTeamMember, user?.id, employeeId],
    queryFn: () => getMyTeamMember(employeeId!),
    enabled: !!user && isManager && !!employeeId,
    retry: false,
    select: (res) => res.data,
  });
}

export function useMyTeamMemberBalances(employeeId: string | undefined) {
  const { user } = useEmployeePortalAuth();
  const isManager = useIsManager();
  return useQuery({
    queryKey: [QueryKeys.MyTeamMemberBalances, user?.id, employeeId],
    queryFn: () => getMyTeamMemberBalances(employeeId!),
    enabled: !!user && isManager && !!employeeId,
    retry: false,
    select: (res) => res.data,
  });
}

export function useMyTeamMemberLeaveRequests(
  employeeId: string | undefined,
  params: MyTeamLeaveRequestsParams
) {
  const { user } = useEmployeePortalAuth();
  const isManager = useIsManager();
  return useQuery({
    queryKey: [
      QueryKeys.MyTeamMemberLeaveRequests,
      user?.id,
      employeeId,
      params,
    ],
    queryFn: () => getMyTeamMemberLeaveRequests(employeeId!, params),
    enabled: !!user && isManager && !!employeeId,
    retry: false,
    placeholderData: keepPreviousData,
    select: (res) => res.data,
  });
}

export function useMyTeamCalendar(params: MyTeamCalendarParams) {
  const { user } = useEmployeePortalAuth();
  const isManager = useIsManager();
  return useQuery({
    queryKey: [QueryKeys.MyTeamCalendar, user?.id, params],
    queryFn: () => getMyTeamCalendar(params),
    enabled: !!user && isManager && !!params.startDate && !!params.endDate,
    placeholderData: keepPreviousData,
  });
}

/** Downloads the list view as CSV; `exporting` drives the button state. */
export function useMyTeamExport() {
  const [exporting, setExporting] = useState(false);
  const download = useCallback(
    async (params: Omit<MyTeamListParams, 'page' | 'limit'>) => {
      setExporting(true);
      try {
        const blob = await exportMyTeam(params);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `my-team-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } finally {
        setExporting(false);
      }
    },
    []
  );
  return { download, exporting };
}

/**
 * "Notify" on a line of the manager's feed. Refetches the feed so the button
 * reads "Pinged just now"; errors are toasted by the API client.
 */
export function usePingMyTeamAttention(onSent?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => pingMyTeamAttention(itemId),
    onSuccess: () => onSent?.(),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: [QueryKeys.MyTeamAttention] }),
  });
}

/** Show lines this manager hid again. */
export function useUnhideMyTeamAttention() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemIds: string[]) => unhideMyTeamAttention(itemIds),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: [QueryKeys.MyTeamAttention] }),
  });
}

/** A hide: the line, plus (signal lines only) how long and why. */
export interface HideMyTeamAttentionVariables extends NeedsAttentionHideOptions {
  itemId: string;
}

/**
 * Hide a line of the manager's feed for them only (JP, 2026-10-03): the row
 * drops at once, then a toast offers Undo. A signal line may be hidden for
 * 30 or 90 days (smart signals, spec 8.2).
 */
export function useHideMyTeamAttention() {
  const queryClient = useQueryClient();
  const { user } = useEmployeePortalAuth();
  const { t } = useTranslation(TranslationNS.MyTeam);
  const { mutate: unhide } = useUnhideMyTeamAttention();
  return useMutation({
    mutationFn: ({ itemId, ...options }: HideMyTeamAttentionVariables) =>
      hideMyTeamAttention(itemId, options),
    onMutate: async ({ itemId }) => {
      const key = [QueryKeys.MyTeamAttention, user?.id];
      await queryClient.cancelQueries({ queryKey: key });
      queryClient.setQueryData<ApiResponse<NeedsAttentionFeed>>(key, (res) =>
        res?.data
          ? { ...res, data: hideNeedsAttentionLocally(res.data, itemId) }
          : res
      );
    },
    onSuccess: ({ data }) => {
      if (!data.itemIds.length) return;
      toastNeedsAttentionHidden({
        message: t('needs_attention.hidden_toast', {
          count: data.itemIds.length,
        }),
        undoLabel: t('needs_attention.undo'),
        onUndo: () => unhide(data.itemIds),
      });
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: [QueryKeys.MyTeamAttention] }),
  });
}

/** A team member's probation card (2026-10-11). */
export function useMyTeamMemberProbation(employeeId: string | undefined) {
  const { user } = useEmployeePortalAuth();
  return useQuery({
    queryKey: [QueryKeys.MyTeamMemberProbation, user?.id, employeeId],
    queryFn: () => getMyTeamMemberProbation(employeeId!),
    enabled: !!user && !!employeeId,
    select: (res) => res.data,
  });
}

/**
 * Confirm, extend or discuss a probation from My team. Refreshes the feed
 * and the member's probation card; errors are toasted by the API client.
 */
export function useMyTeamProbationAction(employeeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: MyTeamProbationPayload) =>
      actOnMyTeamProbation(employeeId, payload),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [QueryKeys.MyTeamAttention],
      });
      void queryClient.invalidateQueries({
        queryKey: [QueryKeys.MyTeamMemberProbation],
      });
    },
  });
}
